import Symbol from './Symbol';
import Transformation from '../geometry/Transformation';
import GeometryFactory from '../geometry/GeometryFactory';

/**
 * 点符号基类,所有点符号均从此类派生,支持符号旋转和平移,先旋转后平移.
 */
export default class MarkerSymbol extends Symbol {
    /**
     * 重写父类初始化方法
     * @param {Object} options - 符号设置的属性
     * @returns {undefined}
     */
    constructor(options) {
        super(options);

        this.color = 'black';
        this.opacity = 1;
        this.offsetX = 0;
        this.offsetY = 0;
        this.angle = 0;
        this.outLine = null;

        // 默认重置变换矩阵
        this.resetTransform = true;

        this.transformation = Transformation.getInstance();
    }

    /**
     * 重写父类方法,设置符号属性.
     * @param {Object} json - 符号设置的属性
     * @returns {undefined}
     */
    fromJson(json) {
        super.fromJson(json);

        this._setValue('color', json.color);
        this._setValue('opacity', json.opacity);
        this._setValue('offsetX', json.offsetX);
        this._setValue('offsetY', json.offsetY);
        this._setValue('angle', json.angle);
        this._setValue('outLine', json.outLine);
    }

    /**
     * 重写父类方法,导出符号属性.
     * @returns {Object} json - 符号的属性
     */
    toJson() {
        const json = super.toJson();

        json.color = this.color;
        json.opacity = this.opacity;
        json.offsetX = this.offsetX;
        json.offsetY = this.offsetY;
        json.angle = this.angle;
        json.outLine = this.outLine;
        
        return json;
    }

    /**
     * 覆盖父类方法，绘制符号
     * @param {Object} ctx - 设备上下文,通过canvas.getContext('2d')方法获得.
     * @returns {undefined}
     */
    draw(ctx, geometry) {
        if (!geometry) {
            return;
        }

        // 保存当前状态，方便绘制完成后恢复状态
        this._save(ctx);

        this._setTransformation(ctx, geometry);

        this._drawSymbol(ctx, geometry);

        // 绘制完成后恢复到上次保存的状态，避免影响以后的绘制
        this._restore(ctx);
    }

    /**
     * 设置变换矩阵.
     * @param {Object} ctx - 设备上下文,通过canvas.getContext('2d')方法获得.
     * @return {undefined}
     */
    _setTransformation(ctx, geometry) {
        if (this.resetTransform) {
            this.transformation.resetTransform();
            ctx.resetTransform();
        }

        this.transformation.translate(geometry.x, geometry.y);
        this.transformation.translate(this.offsetX, this.offsetY);
        this.transformation.rotate(this.angle);

        ctx.translate(geometry.x, geometry.y);
        ctx.translate(this.offsetX, this.offsetY);
        ctx.rotate((this.angle * Math.PI) / 180);
    }

    /**
     * 保存绘制环境状态.
     * @param {Object} ctx - 设备上下文,通过canvas.getContext('2d')方法获得.
     * @return {undefined}
     */
    _save(ctx) {
        this.transformation.save();
        ctx.save();
    }

    /**
     * 恢复绘制环境状态.
     * @param {Object} ctx - 设备上下文,通过canvas.getContext('2d')方法获得.
     * @return {undefined}
     */
    _restore(ctx) {
        this.transformation.restore();
        ctx.restore();
    }

    /**
     * 绘制符号,先绘制符号内容，再绘制符号轮廓.
     * @param {Object} ctx - 设备上下文,通过canvas.getContext('2d')方法获得.
     * @return {undefined}
     */
    _drawSymbol(ctx, geometry) {
        // 绘制符号内容
        this._drawContent(ctx, geometry);

        // 绘制符号轮廓线
        if (this.outLine) {
            this._drawOutLine(ctx, geometry);
        }
    }

    /**
     * 绘制符号内容
     * @abstract
     * @param {Object} ctx - 设备上下文,通过canvas.getContext('2d')方法获得.
     * @return {undefined}
     */
    _drawContent(ctx, geometry) {}

    /**
     * 绘制符号轮廓.
     * @param {Object} ctx - 设备上下文,通过canvas.getContext('2d')方法获得.
     * @return {undefined}
     */
    _drawOutLine(ctx, geometry) {
        // 设置绘制环境
        ctx.strokeStyle = this.outLine.color;
        ctx.lineWidth = this.outLine.width;

        // 获取绘制内容
        const outline = this._getOutLineGeometry(geometry);

        ctx.beginPath();

        // 绘制
        this._drawLineString(ctx, outline);
        ctx.closePath();

        ctx.stroke();
    }

    /**
     * 创建符号内容几何.
     * @abstract
     * @returns {Object} null
     */
    _getContentGeometry(geometry) {
        return null;
    }

    /**
     * 返回符号在局部坐标系下的bound
     * x方向为宽度，y方向为高度.
     * @returns {Object}.
     */
    getOriginBound(geometry) {
        return null;
    }

    /**
     * 返回符号轮廓线.
     * @returns {String} lineString
     */
    _getOutLineGeometry(geometry) {
        // 构造符号外包框
        const bound = this.getOriginBound(geometry);
        const lineString = bound.toLineString();

        return lineString;
    }

    /**
     * 返回符号旋转，平移后的外包框.
     * @returns {Object} bound
     */
    getBound(geometry) {
        // 构造符号外包框
        const originBound = this.getOriginBound(geometry).toLineString();

        // 转换外包框坐标
        const transformedBound = this.transformation.transform(originBound);

        // 获取转换后外包框
        return transformedBound.getBound();
    }
}
